import React from 'react';
import { View, Text, Link, StyleSheet } from '@react-pdf/renderer';
import { startCase } from 'lodash';

/**
 * MUI Fields section components that replace the styled-components Fields section
 * 
 * This file provides Material UI equivalents of the original Fields section components
 * with similar props and functionality. They're used for PDF generation.
 */

// Base interface for field props
interface FieldBaseProps {
  label: string;
}

// Interface for string field props
interface MuiStringFieldProps extends FieldBaseProps {
  content: string;
  vf?: boolean;
}

// Interface for link field props
interface MuiLinkFieldProps extends FieldBaseProps {
  content: {
    src?: string;
    text: string;
  };
}

// Interface for value field props
interface MuiValueFieldProps extends FieldBaseProps {
  content: string;
}

// Define styles for PDF fields
const styles = StyleSheet.create({
  detail: {
    flexDirection: 'row',
    justifyContent: 'flex-start',
    marginBottom: 2,
  },
  label: {
    fontWeight: 'bold',
    marginRight: 4,
  },
});

/**
 * MuiStringField component
 * 
 * @param {MuiStringFieldProps} props - Component props
 * @param {string} props.label - Field label
 * @param {string} props.content - Field content
 * @param {boolean} props.vf - Whether to format as value field
 * @returns {JSX.Element} MUI StringField component
 */
export const MuiStringField: React.FC<MuiStringFieldProps> = ({ label, content, vf = false }) => (
  <View style={styles.detail}>
    <Text style={styles.label}>{`${startCase(label)}: `}</Text>
    <Text>{vf ? content : startCase(content)}</Text>
  </View>
);

/**
 * MuiLinkField component
 * 
 * @param {MuiLinkFieldProps} props - Component props
 * @param {string} props.label - Field label
 * @param {Object} props.content - Field content with src and text
 * @returns {JSX.Element} MUI LinkField component
 */
export const MuiLinkField: React.FC<MuiLinkFieldProps> = ({ label, content }) => {
  const { src = window.location.href, text } = content;
  return (
    <View style={styles.detail}>
      <Text style={styles.label}>{`${startCase(label)}: `}</Text>
      <Link src={src}>{text}</Link>
    </View>
  );
};

/**
 * MuiValueField component
 * 
 * @param {MuiValueFieldProps} props - Component props
 * @param {string} props.label - Field label
 * @param {string} props.content - Field content
 * @returns {JSX.Element} MUI ValueField component
 */
export const MuiValueField: React.FC<MuiValueFieldProps> = ({ label, content }) => (
  <MuiStringField label={label} content={content} vf={true} />
);

export default {
  MuiStringField,
  MuiLinkField,
  MuiValueField
};
